// Segment loads device-mode destinations fire-and-forget, so a failed load leaves each
// ActionDestination's internal `loadPromise` rejected and unhandled. This plugin must be
// registered in the same `register()` batch as the destinations so it can mark them handled.
import { logger } from '@heidi/runtime/logger';
import type { Plugin } from '@segment/analytics-next';

type SegmentAnalytics = Parameters<Plugin['load']>[1];

interface DeferredLoad {
  promise: Promise<unknown>;
}

// `loadPromise` is an internal of analytics-next's ActionDestination, so read
// it structurally: if a future SDK version drops it, this becomes a no-op.
const getLoadPromise = (plugin: unknown): Promise<unknown> | undefined => {
  if (typeof plugin !== 'object' || plugin === null || !('loadPromise' in plugin)) {
    return undefined;
  }
  const { loadPromise } = plugin as { loadPromise?: Partial<DeferredLoad> };
  const promise = loadPromise?.promise;
  return promise && typeof promise.catch === 'function' ? promise : undefined;
};

const ignoreRejection = (): void => {
  // Segment's initialization_failure path already handles the failure.
};

export const SEGMENT_DESTINATION_LOAD_GUARD_NAME = 'Heidi Destination Load Guard';

export const createSegmentDestinationLoadGuard = (): Plugin => {
  let loaded = false;
  return {
    name: SEGMENT_DESTINATION_LOAD_GUARD_NAME,
    type: 'utility',
    version: '1.0.0',
    isLoaded: () => loaded,
    load: (_ctx, analytics: SegmentAnalytics) => {
      // The re-mark in the listener below is load-bearing; this pass is a backstop.
      for (const plugin of analytics.queue.plugins) {
        void getLoadPromise(plugin)?.catch(ignoreRejection);
      }

      // One failed destination emits once per action plugin; report it once.
      const reported = new Set<string>();
      analytics.queue.on('initialization_failure', (plugin) => {
        void getLoadPromise(plugin)?.catch(ignoreRejection);
        if (reported.has(plugin.name)) return;
        reported.add(plugin.name);
        // Destination name only: a Segment workspace constant such as
        // "Braze Web Mode (Actions)". The SDK error is never forwarded.
        logger.warn('segment_destination_load_failed', { destination: plugin.name });
      });

      loaded = true;
      return Promise.resolve();
    },
  };
};
